/**
 * The native `text` widget's whole-widget styling: the same settings, value ranges and defaults as
 * a portable CMS (akarya-portable-cms) text region, kept in `options.portableCms` under the portable
 * CMS's own field names and enum values. The admin editor writes it; the player runtime and the
 * editor's canvas preview read it through {@link textWidgetStyle}, so all three agree. A key that is
 * missing keeps the text widget's look from before these settings existed.
 */

export type TextFont = 'SANS' | 'SERIF' | 'MONO' | 'HANDWRITING';
export type TextAlign = 'START' | 'CENTER' | 'END';
export type TextVerticalAlign = 'TOP' | 'CENTER' | 'BOTTOM';

export const TEXT_FONTS: readonly TextFont[] = ['SANS', 'SERIF', 'MONO', 'HANDWRITING'];
export const TEXT_ALIGNS: readonly TextAlign[] = ['START', 'CENTER', 'END'];
export const TEXT_VERTICAL_ALIGNS: readonly TextVerticalAlign[] = ['TOP', 'CENTER', 'BOTTOM'];

/** Text size in sp: 1sp is 1/540 of the layout's short side. Same range and step as the portable CMS. */
export const TEXT_SIZE_SP = { min: 12, max: 120, step: 4 } as const;
/** Running-text speed, in sp per second. Same range, step and default as the portable CMS. */
export const TEXT_SPEED_SP_PER_SECOND = { min: 20, max: 400, step: 20, default: 80 } as const;
/** Static-text line height as a percentage of the text size. Same range and step as the portable CMS. */
export const TEXT_LINE_SPACING_PERCENT = { min: 80, max: 200, step: 10 } as const;
/** The line height a text widget had before line spacing was a setting. */
export const TEXT_DEFAULT_LINE_SPACING_PERCENT = 130;

/** CSS font stacks for the portable CMS's generic families (no font files to ship, like there). */
export const TEXT_FONT_FAMILY: Record<TextFont, string> = {
  SANS: 'system-ui, sans-serif',
  SERIF: 'Georgia, "Times New Roman", serif',
  MONO: 'ui-monospace, Consolas, monospace',
  HANDWRITING: 'cursive',
};

export interface TextWidgetStyle {
  /** One line scrolling across the widget (the portable CMS's "Running text") instead of static text. */
  running: boolean;
  /** Undefined: the widget's default size, 4% of the layout width. */
  sizeSp: number | undefined;
  /** Undefined: the default weight (semi-bold plain text, normal-weight rich text). */
  bold: boolean | undefined;
  italic: boolean;
  underline: boolean;
  shadow: boolean;
  font: TextFont;
  align: TextAlign;
  verticalAlign: TextVerticalAlign;
  lineSpacingPercent: number;
  speedSpPerSecond: number;
  /** Running text scrolls left to right instead of right to left. */
  reverse: boolean;
}

/** The `options.portableCms` keys, as the portable CMS names them. */
export interface PortableTextOptions {
  running?: boolean;
  textSizeSp?: number;
  textBold?: boolean;
  textItalic?: boolean;
  textUnderline?: boolean;
  textShadow?: boolean;
  textFont?: TextFont;
  textAlign?: TextAlign;
  textVerticalAlign?: TextVerticalAlign;
  textLineSpacingPercent?: number;
  textSpeedDpPerSecond?: number;
  textReverse?: boolean;
}

function clampInt(value: unknown, range: { min: number; max: number }): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(range.max, Math.max(range.min, Math.round(value)));
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/** The text widget's styling from its `options`, with out-of-range or junk values clamped or ignored. */
export function textWidgetStyle(options: unknown): TextWidgetStyle {
  const bag = (options as { portableCms?: unknown } | undefined)?.portableCms;
  const p = (bag && typeof bag === 'object' ? bag : {}) as Record<string, unknown>;
  return {
    running: p.running === true,
    sizeSp: clampInt(p.textSizeSp, TEXT_SIZE_SP),
    bold: typeof p.textBold === 'boolean' ? p.textBold : undefined,
    italic: p.textItalic === true,
    underline: p.textUnderline === true,
    shadow: p.textShadow === true,
    font: oneOf(p.textFont, TEXT_FONTS, 'SANS'),
    align: oneOf(p.textAlign, TEXT_ALIGNS, 'CENTER'),
    verticalAlign: oneOf(p.textVerticalAlign, TEXT_VERTICAL_ALIGNS, 'CENTER'),
    lineSpacingPercent: clampInt(p.textLineSpacingPercent, TEXT_LINE_SPACING_PERCENT) ?? TEXT_DEFAULT_LINE_SPACING_PERCENT,
    speedSpPerSecond: clampInt(p.textSpeedDpPerSecond, TEXT_SPEED_SP_PER_SECOND) ?? TEXT_SPEED_SP_PER_SECOND.default,
    reverse: p.textReverse === true,
  };
}

/** `text-align` for a {@link TextAlign}. */
export function textAlignCss(align: TextAlign): 'left' | 'center' | 'right' {
  return align === 'START' ? 'left' : align === 'END' ? 'right' : 'center';
}

/** Flexbox `align-items` placing a static text block per {@link TextVerticalAlign}. */
export function textVerticalAlignCss(align: TextVerticalAlign): 'flex-start' | 'center' | 'flex-end' {
  return align === 'TOP' ? 'flex-start' : align === 'BOTTOM' ? 'flex-end' : 'center';
}

/** The CSS font-weight for a text widget, given whether it shows rich text. */
export function textFontWeight(style: TextWidgetStyle, rich: boolean): number {
  if (style.bold !== undefined) return style.bold ? 700 : 400;
  return rich ? 400 : 600;
}

/**
 * The portable CMS's text shadow: scaled with the text, light behind dark text and dark behind
 * light text. A color that isn't #rgb/#rrggbb counts as light (the widget's default is white).
 */
export function textShadowCss(color: string): string {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim())?.[1];
  let dark = false;
  if (hex) {
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
    const [r, g, b] = [0, 2, 4].map((i) => {
      const c = parseInt(full.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    }) as [number, number, number];
    dark = 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.5;
  }
  return `0.05em 0.05em 0.08em ${dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'}`;
}
